import { LorebookEntryFixStatus, LorebookEntryStatus, ModerationStatus } from "@next-platform/packages-ts-types";
/**
 * Entries-list sort options, shared by both platforms.
 *
 * PRIORITY is the server's own ordering — every entries request is served with the entries failing
 * the entry requirements first — so it is a no-op here rather than a client-side reordering. That is
 * also what keeps it correct across a server-paginated list: re-sorting locally could only reshuffle
 * the pages already fetched and could never surface a flagged entry from one that has not been.
 */
export var EntrySortingType;
(function (EntrySortingType) {
    EntrySortingType["PRIORITY"] = "PRIORITY";
    EntrySortingType["LATEST"] = "LATEST";
    EntrySortingType["OLDEST"] = "OLDEST";
    EntrySortingType["NAME"] = "NAME";
})(EntrySortingType || (EntrySortingType = {}));
/**
 * The Priority option exists only to resolve moderation problems, so it is dropped from the sort menu
 * once every entry is approved — there is nothing left to prioritise. The dropdown itself remains,
 * since Latest/Oldest/Name are still useful on a clean lorebook.
 *
 * These counts must come from lorebook metadata (num_rejected_entries, num_needs_review_entries,
 * num_flagged_entries) rather than the loaded rows: on a paginated list, counting what is on screen
 * would miss flagged entries sitting on a page that has not been fetched. numFlaggedEntries covers
 * moderator-flagged entries, which the import counts do not. numNeedsFixEntries covers admin-flagged
 * staged ops (pending_ops_summary.needs_fix).
 */
export function hasPriorityEntries(numRejectedEntries, numNeedsReviewEntries, numFlaggedEntries = 0, numNeedsFixEntries = 0) {
    return numRejectedEntries > 0 || numNeedsReviewEntries > 0 || numFlaggedEntries > 0 || numNeedsFixEntries > 0;
}
/**
 * Does this entry fail the entry requirements? Mirrors the warning rule the row badges use: an entry
 * is a priority if moderation rejected it, flagged it for review, its report asks for a fix, or a
 * staged op carries needs_fix.
 */
export function isPriorityEntry(entry) {
    const status = entry?.status;
    const normalized = typeof status === "string" ? status.toLowerCase() : undefined;
    return (normalized === LorebookEntryStatus.REJECTED ||
        normalized === LorebookEntryStatus.NEEDS_REVIEW ||
        entry?.moderationReportStatus === ModerationStatus.FixRequired ||
        entry?.fix_status === LorebookEntryFixStatus.NEEDS_FIX);
}
function toValidTimestamp(candidate) {
    if (candidate === undefined || candidate === null)
        return null;
    const value = typeof candidate === "number" ? candidate : new Date(candidate).getTime();
    return Number.isNaN(value) ? null : value;
}
/**
 * Best-effort timestamp for time-based sorting.
 *
 * Both camelCase and snake_case keys are probed because the two platforms do not receive identically
 * shaped rows — native sees snake_case on some paths — and an unparseable value must fall through to
 * the next candidate rather than poisoning the sort with NaN.
 */
export function getEntryTimestamp(entry) {
    const candidates = [
        entry.updatedAt,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- wire-format key: native receives snake_case rows on some paths
        entry.updated_at,
        entry.createdAt,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- wire-format key: native receives snake_case rows on some paths
        entry.created_at,
    ];
    for (const candidate of candidates) {
        const value = toValidTimestamp(candidate);
        if (value !== null)
            return value;
    }
    return 0;
}
/**
 * Reorder the entries currently loaded. Generic so callers keep their own row type (e.g. the staged
 * OverlaidEntry) instead of widening to LorebookEntry and losing the badge marker.
 */
export function sortEntries(entries, sortType) {
    switch (sortType) {
        case EntrySortingType.LATEST:
            return [...entries].sort((first, second) => getEntryTimestamp(second) - getEntryTimestamp(first));
        case EntrySortingType.OLDEST:
            return [...entries].sort((first, second) => getEntryTimestamp(first) - getEntryTimestamp(second));
        case EntrySortingType.NAME:
            return [...entries].sort((first, second) => (first.name || "").localeCompare(second.name || ""));
        case EntrySortingType.PRIORITY:
        default:
            // Server order already is priority order.
            return entries;
    }
}
